import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Page, Header, Alert } from 'components';
import { Grid, Button, ButtonGroup, Popper, Paper, MenuItem, MenuList, Grow, ClickAwayListener } from '@mui/material';
import _ from 'underscore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';

import PagerDutyIncidentsTable from './components/PagerDutyIncidentsTable';

import {
	GetPagerDutyIncidentsUpdate,
	GetPagerDutyIncidentsAckUpdate,
	GetPagerDutyIncidentsTable,
	DownloadPagerDutyIncidents,
	DownloadCurrentWeekIncidents,
	ToggleIncidentTrackerDialog,
	ToggleDeleteIncidentDialog
} from '../actions/PagerDutyIncidentsActions.js';
import PagerDutyIncidentDialog from './components/PagerDutyIncidentDialog';
import PagerDutyDeleteIncidentDialog from './components/PagerDutyDeleteIncidentDialog';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(3),
	},
	container: {
		marginTop: theme.spacing(3),
	}
}));

const options = ['Export all', 'Export current week'];

const PagerDutyIncidents = () => {
	const [open, setOpen] = React.useState(false);
	const anchorRef = useRef(null);
	const classes = useStyles();
	const dispatch = useDispatch();
	const incidentsTableData = useSelector((state) => state.pagerDutyIncidentsReducer.incidentsTableData);
	const incidentsLastUpdate = useSelector((state) => state.pagerDutyIncidentsReducer.incidentsLastUpdate);
	const incidentsAckLastUpdate = useSelector((state) => state.pagerDutyIncidentsReducer.incidentsAckLastUpdate);
	const incidentTrackerDialogVisibility = useSelector((state) => state.pagerDutyIncidentsReducer.incidentTrackerDialogVisibility);
	const deleteIncidentDialogVisibility = useSelector((state) => state.pagerDutyIncidentsReducer.deleteIncidentDialogVisibility);
	const selectedIncidentId = useSelector((state) => state.pagerDutyIncidentsReducer.selectedIncidentId);

	const _onExportClick = () => {
		dispatch(DownloadPagerDutyIncidents());
	};

	const _onCloseIncidentTrackerDialog = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleIncidentTrackerDialog(false, 0));
		}
	};

	const _onCloseDeleteIncidentDialog = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleDeleteIncidentDialog(false, 0));
		}
	};

	const _onCreateIncidentClick = () => {
		dispatch(ToggleIncidentTrackerDialog(true, 0));
	};

	const handleMenuItemClick = (event, index) => {
		if (index === 0) {
			_onExportClick();
		} else {
			dispatch(DownloadCurrentWeekIncidents());
		}
		setOpen(false);
	};

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event) => {
		if (anchorRef.current && anchorRef.current.contains(event.target)) {
			return;
		}

		setOpen(false);
	};

	useEffect(() => {
		dispatch(GetPagerDutyIncidentsUpdate());
		dispatch(GetPagerDutyIncidentsAckUpdate());
		dispatch(GetPagerDutyIncidentsTable());
	}, []);

	return (
		<Page className={classes.root} title="PagerDuty Incidents">
			<Alert
				className={classes.container}
				variant="info"
				message={_.isUndefined(incidentsLastUpdate) ? 'Incidents date unavailable!' : `Incidents Last Update: ${incidentsLastUpdate}  -  Incidents Ack Last Update: ${incidentsAckLastUpdate}`}
			/>
			<br />
			<Grid container spacing={2}>
				<Grid item md={6} lg={6} xs={6}>
					<Button
						className={classes.agreeButton}
						color="success"
						onClick={_onCreateIncidentClick}
						variant="contained"
						size="medium"
						startIcon={<AddIcon />}
					>
						Create Incident
					</Button>
				</Grid>
				<Grid item md={6} lg={6} xs={6} style={{ display: 'inline-flex', justifyContent: 'end' }}>
					<ButtonGroup
						variant="contained"
						ref={anchorRef}
						aria-label="split button"
						color="inherit"
					>
						<Button
							className={classes.agreeButton}
							color="inherit"
							variant="contained"
							size="medium"
							startIcon={<FileDownloadIcon />}
							onClick={_onExportClick}
						>
							Export Incidents
						</Button>
						<Button
							size="small"
							color="inherit"
							aria-controls={open ? 'split-button-menu' : undefined}
							aria-expanded={open ? 'true' : undefined}
							aria-label="Export Incidents"
							aria-haspopup="menu"
							onClick={handleToggle}
						>
							<ArrowDropDownIcon />
						</Button>
					</ButtonGroup>
					<Popper
						sx={{
							zIndex: 1,
						}}
						open={open}
						anchorEl={anchorRef.current}
						role={undefined}
						transition
						disablePortal
					>
						{({ TransitionProps, placement }) => (
							<Grow
								{...TransitionProps}
								style={{
									transformOrigin:
									placement === 'bottom' ? 'center top' : 'center bottom',
								}}
							>
								<Paper>
									<ClickAwayListener onClickAway={handleClose}>
										<MenuList id="split-button-menu" autoFocusItem>
											{options.map((option, index) => (
												<MenuItem
													key={option}
													onClick={(event) => handleMenuItemClick(event, index)}
												>
													{option}
												</MenuItem>
											))}
										</MenuList>
									</ClickAwayListener>
								</Paper>
							</Grow>
						)}
					</Popper>
				</Grid>
			</Grid>
			<Grid item className={clsx(classes.container)} lg={12} xs={12}>
				<PagerDutyIncidentsTable data={incidentsTableData} />
			</Grid>
			<Grid className={classes.container} container spacing={3}>
				<Grid item lg={12} xs={12}>
					{incidentTrackerDialogVisibility && (
						<PagerDutyIncidentDialog
							onClose={_onCloseIncidentTrackerDialog}
							open={incidentTrackerDialogVisibility}
							incidentId={selectedIncidentId}
						/>
					)}
					{deleteIncidentDialogVisibility && (
						<PagerDutyDeleteIncidentDialog
							onClose={_onCloseDeleteIncidentDialog}
							open={deleteIncidentDialogVisibility}
							incidentId={selectedIncidentId}
						/>
					)}
				</Grid>
			</Grid>
		</Page>
	);
};

export default PagerDutyIncidents;
